import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  EducatorTypeEnum,
  ProfessionalTypeEnum,
  ProsListGetProsQuery,
  ProsListGetProsQueryVariables,
} from '@gql/graphql';
import { CountryCode, SortOrder } from '@gql/graphql';
import type { MapBounds, MapCoordinates } from '@presentation/pages/ProList/components/Map/types';
import { useMemo } from 'react';
import { PAGINATED_SIZE, PROS_MAP_INITIAL_VALUES } from '../constants';

export const PROS_LIST_GET_PROS = graphql(`
  query ProsListGetPros(
    $take: Int
    $skip: Int
    $where: ProfessionalWhereInput
    $orderBy: [ProfessionalOrderByWithRelationAndSearchRelevanceInput!]
    $serviceId: Int
    $nearByPoint: LatLngPoint
    $maxDistance: Int
    $nearByAddress: String
  ) {
    searchProfessionals(
      where: $where
      take: $take
      skip: $skip
      nearByPoint: $nearByPoint
      nearByAddress: $nearByAddress
      serviceId: $serviceId
      maxDistance: $maxDistance
      orderBy: $orderBy
    ) {
      professionals {
        id
        type
        centerType
        educatorType
        type
        email
        spokenLanguages
        centerType
        distance
        onlineBookingEnabled
        unclaimedProfile

        imgs(take: 1) {
          id
          small
        }
        reviews {
          id
          grade
        }
        address {
          id
          city
          lat
          lng
        }

        translations {
          id
          language
          url
          title
          description
        }
      }
      totalCount
    }
  }
`);

export interface UseGetProsParams {
  page: number;
  type: ProfessionalTypeEnum;
  serviceId?: number;
  forceSkip?: boolean;
  nearByPoint?: MapCoordinates;
  maxDistance?: number;
  bounds?: MapBounds;
  nearByAddress?: string;
  shouldUseLocationSearch?: boolean;
  spokenLanguages?: string[];
  hasBooking?: boolean;
  educatorTypes?: EducatorTypeEnum[];
}

export const useGetPros = (params: UseGetProsParams) => {
  const {
    page,
    type,
    serviceId,
    nearByPoint,
    forceSkip,
    maxDistance,
    bounds,
    nearByAddress,
    shouldUseLocationSearch,
    spokenLanguages,
    hasBooking,
    educatorTypes,
  } = params;

  const { countryCode } = useRouter();

  const variables = useMemo((): ProsListGetProsQueryVariables => {
    return {
      ...(!shouldUseLocationSearch && {
        orderBy: [{ orderingIndex: SortOrder.asc }],
      }),
      ...(serviceId && { serviceId }),
      maxDistance: maxDistance ?? PROS_MAP_INITIAL_VALUES[countryCode].maxDistance,
      ...(nearByPoint && { nearByPoint }),
      nearByAddress,

      where: {
        AND: [
          { type: { equals: type } },
          { published: { equals: true } },
          { ...(hasBooking != null && { onlineBookingEnabled: { equals: hasBooking } }) },
          { ...(spokenLanguages?.length && { spokenLanguages: { hasSome: spokenLanguages } }) },
          {
            ...(!shouldUseLocationSearch &&
              !!bounds && {
                address: {
                  is: {
                    AND: [
                      { lat: { lte: bounds.northEast.lat } },
                      { lat: { gte: bounds.southWest.lat } },
                      { lng: { lte: bounds.northEast.lng } },
                      { lng: { gte: bounds.southWest.lng } },
                    ],
                  },
                },
              }),
          },
          {
            ...(educatorTypes?.length && { educatorType: { in: educatorTypes } }),
          },
          ...(countryCode && countryCode !== CountryCode.EU
            ? [{ address: { is: { country: { equals: countryCode } } } }]
            : []),
        ],
      },
      take: PAGINATED_SIZE,
      skip: page * PAGINATED_SIZE,
    } as ProsListGetProsQueryVariables;
  }, [
    serviceId,
    maxDistance,
    countryCode,
    nearByPoint,
    nearByAddress,
    type,
    hasBooking,
    spokenLanguages,
    shouldUseLocationSearch,
    bounds,
    educatorTypes,
    page,
  ]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    ProsListGetProsQuery,
    ProsListGetProsQueryVariables
  >(PROS_LIST_GET_PROS, {
    variables,
    skip: forceSkip,
  });

  const totalRecords = data?.searchProfessionals.totalCount || 0; // eslint-disable-line

  return {
    pros: data?.searchProfessionals.professionals ?? [],
    pageCount: Math.ceil(totalRecords / PAGINATED_SIZE),
    prosError: error,
    prosLoading: loading,
    prosVariables: variables,
    prosFetchMore: fetchMore,
    prosRefetch: refetch,
  };
};
